import { computed, ComputedRef, defineAsyncComponent, ref, Ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import modalService from '@/modules/modals/services/modal.service';
import ToastService from '@/modules/toasts/services/ToastService';
import WCSimpleToastVue from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ExternalCoupon from '@/models/entities/items/external-coupon';
import ItemSummary from '@/models/entities/items/item-summary';

interface CouponScanLineItem {
  couponItemScanCodes?: string[];
}

interface ScannedLineItem {
  scancode?: string;
}

interface CouponProviderLineItem {
  couponProviderId?: string;
}

interface UseCouponReturn {
  products: Ref<ItemSummary[]>;
  loading: Ref<boolean>;
  productsLoading: Ref<boolean>;
  error: Ref<Error | null>;
  isClipped: Ref<boolean>;
  isRedeemed: Ref<boolean>;
  loadFailedError: Ref<boolean | null>;
  couponData: Ref<ExternalCoupon>;
  totalCount: Ref<number>;
  isCustomer: ComputedRef<boolean>;
  isGuest: ComputedRef<boolean>;
  availableCoupons: ComputedRef<any>;
  clippedCoupons: ComputedRef<any>;
  redeemedCoupons: ComputedRef<any>;
  getValidCouponAlerts: ComputedRef<any>;
  couponTitle: ComputedRef<string | undefined>;
  listOfUPCs: ComputedRef<string | undefined>;
  evaluateClipStatus: ComputedRef<boolean>;
  evaluateRedeemedStatus: ComputedRef<boolean>;
  productList: ComputedRef<ItemSummary[]>;
  hasExternalCouponCodeAlert: ComputedRef<string | null>;
  hasErrors: ComputedRef<boolean>;
  setAvailableCoupons: (payload: string[]) => Promise<any>;
  setClippedCoupons: (payload: string[]) => Promise<any>;
  setRedeemedCoupons: (payload: string[]) => Promise<any>;
  clip: (couponId: string) => Promise<any>;
  fetchMyCoupons: () => Promise<any>;
  fetchCustomerCoupons: () => Promise<void>;
  fetchEligibleProducts: (couponId: string) => Promise<void>;
  allowedToClip: () => boolean;
  openMoreInfoModal: (couponFilter?: string) => Promise<void>;
  handleClip: () => Promise<void>;
  fetchCouponAndOpenModal: (couponLineItem?: CouponProviderLineItem) => Promise<void>;
  fetchCouponFromId: (couponId?: string) => Promise<void>;
  parseCouponForId: (couponLineItem?: CouponProviderLineItem) => string | undefined;
  getCoupon: (
    cartLineItems?: CouponScanLineItem[],
    lineItem?: ScannedLineItem,
  ) => CouponScanLineItem | null;
}

const useCoupon = (coupon: ExternalCoupon): UseCouponReturn => {
  const store = useStore();
  const { t } = useI18n();

  const products = ref<ItemSummary[]>([]);
  const loading = ref(false);
  const productsLoading = ref(false);
  const error = ref<Error | null>(null);
  const isClipped = ref(false);
  const isRedeemed = ref(false);
  const loadFailedError = ref<boolean | null>(null);
  const couponData = ref(coupon);
  const totalCount = ref(-1);

  const isCustomer = computed(() => store.getters['user/isCustomer']);
  const isGuest = computed(() => store.getters['user/isGuest']);
  const availableCoupons = computed(() => store.getters['coupons/getAvailableCoupons']);
  const clippedCoupons = computed(() => store.getters['coupons/getClippedCoupons']);
  const redeemedCoupons = computed(() => store.getters['coupons/getRedeemedCoupons']);
  const getValidCouponAlerts = computed(() => store.getters['cart/getExternalCouponAlerts']);

  const couponTitle = computed(() => {
    return couponData.value?.name?.includes(':')
      ? couponData.value?.name?.split(':').pop()
      : couponData.value?.name;
  });

  const listOfUPCs = computed(() => couponData.value?.requirementUpcs?.join(', '));

  const evaluateClipStatus = computed(
    () => clippedCoupons.value?.filter(i => i?.includes(couponData.value?.id)).length > 0,
  );

  const evaluateRedeemedStatus = computed(
    () => redeemedCoupons.value?.filter(i => i?.includes(couponData.value?.id)).length > 0,
  );

  const productList = computed(() => products.value);

  const hasExternalCouponCodeAlert = computed(() =>
    getValidCouponAlerts.value?.length > 0 ? 'valid' : null,
  );

  const hasErrors = computed(
    () =>
      hasExternalCouponCodeAlert.value &&
      getValidCouponAlerts.value?.filter(alert => alert?.values?.reason)?.length > 0,
  );

  const setAvailableCoupons = (payload: string[]) =>
    store.dispatch('coupons/setAvailableCoupons', payload);
  const setClippedCoupons = (payload: string[]) =>
    store.dispatch('coupons/setClippedCoupons', payload);
  const setRedeemedCoupons = (payload: string[]) =>
    store.dispatch('coupons/setRedeemedCoupons', payload);
  const clip = (couponId: string) => store.dispatch('coupons/clip', couponId);
  const fetchMyCoupons = () => store.dispatch('coupons/fetchMyCoupons');

  /**
   * Should be called at a Higher order component ie Coupon Browse, Coupon Detail Page
   * in order to populate isClipped state for coupons
   */
  const fetchCustomerCoupons = async () => {
    if (!isGuest.value && isCustomer.value) {
      try {
        await fetchMyCoupons();
      } catch (e) {
        console.error(e);
      }
    }
  };

  /**
   * Populate products / items data for a component
   * Fetch eligible product / item information using a BrowseRequest w/ facets
   */
  const fetchEligibleProducts = async (couponId: string) => {
    productsLoading.value = true;
    try {
      if (couponId) {
        const requestfacets = {
          cp: [couponId],
        };

        const response = await axios.post('api/b', {
          pn: 1,
          ps: 32,
          facets: requestfacets,
        });
        products.value = response?.data.items;
      }
    } catch (e) {
      error.value = e;
      console.error(e);
    } finally {
      productsLoading.value = false;
    }
  };

  const allowedToClip = () => !isGuest.value && isCustomer.value;

  const handleClip = async () => {
    if (!allowedToClip()) {
      ToastService.open(WCSimpleToastVue, {
        props: {
          variant: 'danger',
          title: t('logIn'),
          message: t('showCouponLoginPrompt'),
        },
        timeout: 7000,
        actions: {},
        skipHash: false,
      });
    } else if (allowedToClip() && !isClipped.value) {
      await clip(couponData.value.id);
      if (!evaluateClipStatus.value) {
        ToastService.open(WCSimpleToastVue, {
          props: {
            variant: 'danger',
            title: 'Error Clipping Coupon',
            message: t('showCouponClipFailure'),
          },
          timeout: 7000,
          actions: {},
          skipHash: false,
        });
      } else {
        isClipped.value = true;
      }
    }
  };

  const openMoreInfoModal = async (couponFilter?: string) => {
    try {
      await fetchEligibleProducts(couponData.value.id);
      await fetchCustomerCoupons();
      const couponDetailModal = defineAsyncComponent(() =>
        import('@/modules/coupons/components/WCCouponDetailModal/WCCouponDetailModal.vue'),
      );
      await modalService.open(couponDetailModal, {
        coupon: couponData.value,
        couponTitle: couponTitle.value,
        listOfUPCs: listOfUPCs.value,
        products: products.value,
        couponFilter,
        clip: handleClip,
      });
    } catch (e) {
      if (e !== 'Dismissed')
        ToastService.open(WCSimpleToastVue, {
          props: {
            variant: 'danger',
            title: t('error'),
            message: t('showCouponMoreInfoFailure'),
          },
          timeout: 7000,
          actions: {},
          skipHash: false,
        });
    }
  };

  const parseCouponForId = (couponLineItem?: CouponProviderLineItem): string | undefined =>
    couponLineItem?.couponProviderId;

  const fetchCouponFromId = async (couponId?: string) => {
    try {
      loading.value = true;
      if (couponId) {
        const response = await axios.get(`api/coupon/${couponId}`);
        couponData.value = response.data;
        loadFailedError.value = false;
      }
    } catch (e) {
      error.value = e;
      loadFailedError.value = true;
    } finally {
      loading.value = false;
    }
  };

  const fetchCouponAndOpenModal = async (couponLineItem?: CouponProviderLineItem) => {
    if (couponLineItem) {
      const couponId = parseCouponForId(couponLineItem);
      await fetchCouponFromId(couponId);
      await openMoreInfoModal();
    } else {
      ToastService.open(WCSimpleToastVue, {
        props: {
          variant: 'danger',
          title: t('error'),
          message: t('showCouponMoreInfoFailure'),
        },
        timeout: 7000,
        actions: {},
        skipHash: false,
      });
    }
  };

  const getCoupon = (cartLineItems?: CouponScanLineItem[], lineItem?: ScannedLineItem) => {
    let foundCoupon: CouponScanLineItem | null = null;
    if (cartLineItems && lineItem) {
      cartLineItems.forEach(item => {
        if (item?.couponItemScanCodes) {
          item?.couponItemScanCodes.forEach(scanCode => {
            if (scanCode === lineItem.scancode) foundCoupon = item;
          });
        }
      });
    }
    return foundCoupon;
  };

  return {
    products,
    loading,
    productsLoading,
    error,
    isClipped,
    isRedeemed,
    loadFailedError,
    couponData,
    totalCount,
    isCustomer,
    isGuest,
    availableCoupons,
    clippedCoupons,
    redeemedCoupons,
    getValidCouponAlerts,
    couponTitle,
    listOfUPCs,
    evaluateClipStatus,
    evaluateRedeemedStatus,
    productList,
    hasExternalCouponCodeAlert,
    hasErrors,
    setAvailableCoupons,
    setClippedCoupons,
    setRedeemedCoupons,
    clip,
    fetchMyCoupons,
    fetchCustomerCoupons,
    fetchEligibleProducts,
    allowedToClip,
    openMoreInfoModal,
    handleClip,
    fetchCouponAndOpenModal,
    fetchCouponFromId,
    parseCouponForId,
    getCoupon,
  };
};

export default useCoupon;
